/**
 * Getting started checklist on the Overview. Progress comes from the
 * workspace's real data (onboarding status), so it ticks itself off however
 * the user got there. Hidden once everything's done, or when dismissed.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { onboardingApi, type OnboardingStatus } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";

interface Task {
  key: string;
  title: string;
  hint: string;
  done: boolean;
  /** Where to go to do it; none for things that happen on their own */
  href?: string;
  cta?: string;
}

function tasksFor(s: OnboardingStatus): Task[] {
  // Once setup is done /onboarding redirects away, so point at the pages that do the same job
  const setup = s.completed ? "/know-your-competitors" : "/onboarding";
  return [
    {
      key: "website",
      title: "Add your website",
      hint: "Mira uses it to work out who you compete with.",
      done: !!s.domain,
      href: s.completed ? "/settings?tab=workspace" : "/onboarding",
      cta: "Add website",
    },
    {
      key: "competitors",
      title: "Pick competitors to watch",
      hint: "Choose the companies and pages that matter: pricing, changelog, features.",
      done: s.watchedUrls > 0,
      href: s.hasDiscovery ? "/know-your-competitors" : setup,
      cta: "Pick competitors",
    },
    {
      key: "alerts",
      title: "Choose where alerts go",
      hint: "Email, Slack or both. You'll only hear about meaningful changes.",
      done: s.alertChannels.email || s.alertChannels.slack,
      href: "/settings?tab=notifications",
      cta: "Set up alerts",
    },
    {
      key: "mira",
      title: "Ask Mira about a competitor",
      hint: "Their pricing, positioning or what they've shipped lately.",
      done: s.chats > 0,
      href: "/chat",
      cta: "Ask Mira",
    },
    {
      key: "alert",
      title: "Get your first alert",
      hint: "We check every page hourly and tell you when something changes.",
      done: s.alerts > 0,
    },
  ];
}

const dismissKey = (workspaceId: string) => `ps-getting-started-dismissed:${workspaceId}`;

export function GettingStarted() {
  const { workspace } = useAuth();
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!workspace?.id) return;
    try { setDismissed(localStorage.getItem(dismissKey(workspace.id)) === "1"); } catch { /* shown */ }
    onboardingApi.status().then((r) => { if ("data" in r && r.data) setStatus(r.data); });
  }, [workspace?.id]);

  if (!status || dismissed || !workspace) return null;
  const tasks = tasksFor(status);
  const doneCount = tasks.filter((t) => t.done).length;
  if (doneCount === tasks.length) return null;
  const next = tasks.find((t) => !t.done && t.href);

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(dismissKey(workspace.id), "1"); } catch { /* not persisted */ }
  };

  return (
    <section className="rounded-lg border border-border bg-card">
      <div className="flex items-center gap-4 border-b border-border px-5 py-3">
        <div className="flex items-center gap-2">
          <Icon name="rocket-01" size={12} className="text-muted-foreground" />
          <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Getting started</span>
        </div>
        <div className="flex flex-1 items-center gap-3">
          <div className="h-1 max-w-40 flex-1 overflow-hidden rounded-full bg-foreground/10">
            <div className="h-full rounded-full bg-foreground transition-all" style={{ width: `${(doneCount / tasks.length) * 100}%` }} />
          </div>
          <span className="font-mono text-[11px] tabular-nums text-muted-foreground">{doneCount} / {tasks.length}</span>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Hide getting started"
          className="rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <Icon name="cancel-01" size={12} />
        </button>
      </div>

      <ul>
        {tasks.map((t, i) => {
          const isNext = t === next;
          return (
            <li key={t.key} className={cn("flex items-center gap-3 px-5 py-3", i > 0 && "border-t border-border", isNext && "bg-foreground/[0.02]")}>
              <span className={cn(
                "flex h-4 w-4 shrink-0 items-center justify-center rounded-full",
                t.done ? "bg-foreground text-background" : "border border-border",
              )}>
                {t.done && <Icon name="tick-01" size={10} />}
              </span>
              <div className="min-w-0 flex-1">
                <p className={cn("font-sans text-xs", t.done ? "text-muted-foreground line-through decoration-muted-foreground/40" : "font-medium")}>
                  {t.title}
                </p>
                {!t.done && <p className="font-sans text-[11px] text-muted-foreground">{t.hint}</p>}
              </div>
              {!t.done && t.href && (
                <Link
                  to={t.href}
                  className={cn(
                    "flex shrink-0 items-center gap-1 rounded-md px-2.5 py-1.5 font-mono text-[11px] uppercase tracking-widest transition-colors",
                    isNext ? "bg-foreground text-background hover:bg-foreground/90" : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {t.cta}
                  <Icon name="arrow-right-01" size={11} />
                </Link>
              )}
              {!t.done && !t.href && (
                <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
                  {status.watchedUrls > 0 ? "Watching" : "After setup"}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
